import type { AppConfigOverrides } from '@shared/app-config'
import type { ProxyConfig } from '@shared/wippy-package/wippy-package'
import { IFRAME_MESSAGE_TYPE, IFrameMessageType } from '@shared/iframe-messages'
import globalEvents from '@shared/iframe-messages/global-events'
import { wippyFrameName, wippyFrameTitle } from '@shared/util/iframe-meta'
import { uuid } from '@shared/util/uuid'
import { buildInjectedChildConfig, injectWippyHtml, normalizeRoute } from '@/proxy/shared/html'

type ResourceType = 'page' | 'artifact'

interface SubscriptionRecord {
  topic: string
  off: () => void
}

interface PendingBridgeRequest {
  resolve: (value: any) => void
  reject: (reason?: unknown) => void
  timer: ReturnType<typeof setTimeout>
}

function attrBoolean(val: string | null | undefined): boolean {
  if (val === null || val === undefined)
    return false
  if (val === '')
    return true
  return val === 'true'
}

function directoryUrlFrom(src: string) {
  const url = new URL(src, document.baseURI)
  url.hash = ''
  url.search = ''
  url.pathname = url.pathname.replace(/\/?[^/]*$/, '/')
  return url.toString()
}

function generateStyle(fillContainer = false) {
  const heightRule = fillContainer ? 'height:100%;' : ''
  return `
      :host { display:block; ${heightRule} }
      .loader { display:flex; align-items:center; justify-content:center; padding:1rem; font-size:14px; color:#666; ${heightRule} }
      .error { color: red; padding:1rem; }
      iframe { display:block; width:100%; ${heightRule} border:0; }
    `
}

const observedAttributes = [
  'src',
  'srcdoc',
  'base-url',
  'auto-height',
  'sub-path',
  'nav-owner',
  'resource-id',
  'resource-type',
  'resource-label',
]

export class WIframeElement extends HTMLElement {
  static get observedAttributes() {
    return [...observedAttributes]
  }

  proxyConfig?: ProxyConfig
  configOverrides?: AppConfigOverrides

  private shadow: ShadowRoot
  private styleEl!: HTMLStyleElement
  private loadingEl!: HTMLElement
  private errorEl!: HTMLElement
  private iframe: HTMLIFrameElement | null = null
  private messageListener?: (e: MessageEvent) => void
  private subscriptions: Record<string, SubscriptionRecord> = {}
  private layoutEventListener?: (e: MessageEvent) => void
  private layoutSubCount = 0
  private pendingBridgeRequests = new Map<string, PendingBridgeRequest>()
  // Cleanup hooks for in-flight relay handlers (CmdWsSend / CmdStateGet /
  // CmdStateGetAll). Each entry knows how to remove its `window` message
  // listener and clear its timeout. Drained in `cleanup()` so a rapid
  // src-change or disconnect cannot accumulate stale listeners.
  private activeRelayCleanups: Array<() => void> = []
  private sourceUrl: string | null = null
  private directHtml: string | null = null
  private baseUrl: string | null = null
  private autoHeight = false
  private subPath: string | null = null
  private navOwner = false
  private resourceId: string | null = null
  private resourceType: ResourceType = 'page'
  private resourceLabel: string | null = null
  private loadSeq = 0
  private unbindConfigUpdated?: () => void

  constructor() {
    super()
    this.shadow = this.attachShadow({ mode: 'open' })
    this.styleEl = document.createElement('style')
    this.styleEl.textContent = generateStyle(true)
    this.shadow.appendChild(this.styleEl)

    this.loadingEl = document.createElement('div')
    this.loadingEl.className = 'loader'
    this.loadingEl.textContent = 'Loading...'
    this.loadingEl.setAttribute('part', 'loader')
    this.shadow.appendChild(this.loadingEl)

    this.errorEl = document.createElement('div')
    this.errorEl.className = 'error'
    this.errorEl.style.display = 'none'
    this.errorEl.setAttribute('part', 'error')
    this.shadow.appendChild(this.errorEl)
  }

  get srcdoc() {
    return this.directHtml ?? ''
  }

  get src() {
    return this.sourceUrl ?? ''
  }

  set src(value: string | null) {
    this.sourceUrl = value
    if (value === null) {
      this.removeAttribute('src')
      return
    }
    if (this.getAttribute('src') !== value)
      this.setAttribute('src', value)
  }

  set srcdoc(value: string | null) {
    this.directHtml = value
    if (this.isConnected)
      this.load()
  }

  post(channel: string, payload?: unknown) {
    this.postToChild(IFrameMessageType.OnBridgeRequest, { channel, payload })
  }

  request<T = unknown>(channel: string, payload?: unknown, options?: { timeoutMs?: number }): Promise<T> {
    const requestId = uuid()
    const timeoutMs = options?.timeoutMs ?? 10_000
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingBridgeRequests.delete(requestId)
        reject(new Error(`Bridge request ${requestId} timed out after ${timeoutMs}ms`))
      }, timeoutMs)
      this.pendingBridgeRequests.set(requestId, { resolve, reject, timer })
      this.postToChild(IFrameMessageType.OnBridgeRequest, { uuid: requestId, channel, payload })
    })
  }

  connectedCallback() {
    this.updateInternalState()
    this.unbindConfigUpdated = globalEvents.on('configUpdated', () => {
      this.postConfigUpdate()
    })
    this.load()
  }

  disconnectedCallback() {
    this.unbindConfigUpdated?.()
    this.unbindConfigUpdated = undefined
    this.emitNavOwnerRelease()
    this.cleanup()
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (!this.isConnected)
      return

    // Fast path: nav-owner subroute changes forward as `UrlWasUpdatedInParent`
    // to the existing iframe — no reload. Gate on `name === 'sub-path'` so
    // that other reload-relevant attrs (`src`, `resource-id`, etc.) still
    // fall through to the load path even on a nav-owner iframe.
    if (
      name === 'sub-path'
      && this.navOwner
      && (this.iframe || this.getAttribute('status') === 'loading')
    ) {
      if (oldValue !== null && newValue !== oldValue) {
        this.subPath = newValue
        this.postUrlUpdate(newValue || '/')
      }
      return
    }

    this.updateInternalState()

    // Only attrs that change the loaded CONTENT trigger a full reload.
    // `auto-height` is style-only; `sub-path` / `nav-owner` are consumed
    // on next load; without this gate, dynamic style changes (e.g.
    // turning `auto-height` on after mount) refetch the iframe HTML
    // over the network and remount it — destroying iframe state and
    // breaking any in-flight bridge requests.
    const reloadAttrs = new Set(['src', 'srcdoc', 'base-url', 'resource-id', 'resource-type'])
    if (reloadAttrs.has(name)) {
      this.load()
      return
    }

    if (name === 'auto-height') {
      // Update host style only; iframe content keeps running.
      this.styleEl.textContent = generateStyle(!this.autoHeight)
    }
  }

  private updateInternalState() {
    this.sourceUrl = this.getAttribute('src')
    const attrSrcdoc = this.getAttribute('srcdoc')
    if (attrSrcdoc !== null)
      this.directHtml = attrSrcdoc
    this.baseUrl = this.getAttribute('base-url')
    this.autoHeight = attrBoolean(this.getAttribute('auto-height'))
    this.subPath = this.getAttribute('sub-path')
    this.navOwner = this.hasAttribute('nav-owner')
    this.resourceId = this.getAttribute('resource-id') || this.getAttribute('id')
    this.resourceType = this.getAttribute('resource-type') === 'artifact' ? 'artifact' : 'page'
    this.resourceLabel = this.getAttribute('resource-label')
  }

  private cleanup() {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener)
      this.messageListener = undefined
    }
    Object.values(this.subscriptions).forEach(s => s.off())
    this.subscriptions = {}
    if (this.layoutEventListener) {
      window.removeEventListener('message', this.layoutEventListener)
      this.layoutEventListener = undefined
    }
    if (this.layoutSubCount > 0) {
      this.layoutSubCount = 0
      try {
        window.parent.postMessage(
          JSON.stringify({ type: IFRAME_MESSAGE_TYPE, action: IFrameMessageType.CmdLayoutUnsubscribe }),
          '*',
        )
      }
      catch {
        // parent may already be gone
      }
    }
    this.pendingBridgeRequests.forEach((request) => {
      clearTimeout(request.timer)
      request.reject(new Error('Iframe was unloaded before bridge response arrived'))
    })
    this.pendingBridgeRequests.clear()
    // Drain any in-flight CmdWsSend / CmdStateGet / CmdStateGetAll relay
    // handlers — without this, rapid src changes accumulate `window`
    // message listeners that linger for up to 30s.
    this.activeRelayCleanups.forEach((dispose) => {
      try {
        dispose()
      }
      catch {
        // best-effort cleanup
      }
    })
    this.activeRelayCleanups = []
    this.iframe = null
  }

  private emitNavOwnerClaim() {
    if (!this.navOwner || !this.resourceId)
      return
    window.parent.postMessage(JSON.stringify({
      type: IFRAME_MESSAGE_TYPE,
      action: IFrameMessageType.CmdClaimNavOwner,
      sourceId: this.resourceId,
    }), '*')
  }

  private emitNavOwnerRelease() {
    if (!this.navOwner || !this.resourceId)
      return
    window.parent.postMessage(JSON.stringify({
      type: IFRAME_MESSAGE_TYPE,
      action: IFrameMessageType.CmdReleaseNavOwner,
      sourceId: this.resourceId,
    }), '*')
  }

  private async load() {
    if (!this.sourceUrl && !this.directHtml)
      return

    const seq = ++this.loadSeq
    const oldIframe = this.iframe
    this.cleanup()
    oldIframe?.remove()

    this.setStatus('loading')
    this.styleEl.textContent = generateStyle(!this.autoHeight)
    this.loadingEl.style.display = 'flex'
    this.errorEl.style.display = 'none'
    this.dispatchEvent(new CustomEvent('loading'))

    try {
      const html = await this.resolveHtml()
      if (seq !== this.loadSeq)
        return
      await this.renderIframe(html)
      if (seq !== this.loadSeq)
        return
      this.setStatus('ready')
      this.loadingEl.style.display = 'none'
      this.dispatchEvent(new CustomEvent('load'))
    }
    catch (err) {
      if (seq !== this.loadSeq)
        return
      this.setStatus('error')
      this.errorEl.textContent = (err as Error).message || 'Failed to load iframe'
      this.loadingEl.style.display = 'none'
      this.errorEl.style.display = 'block'
      this.dispatchEvent(new CustomEvent('error', { detail: err }))
    }
  }

  private async resolveHtml() {
    if (this.sourceUrl) {
      const { api } = await window.getWippyApi()
      const response = await api.get<string>(this.sourceUrl, {
        responseType: 'text',
        transformResponse: [data => data],
      })
      return this.injectHtml(response.data, this.baseUrl || directoryUrlFrom(this.sourceUrl))
    }
    return this.injectHtml(this.directHtml || '', this.baseUrl || document.baseURI)
  }

  private injectHtml(sourceHtml: string, baseUrl?: string) {
    return injectWippyHtml(sourceHtml, {
      baseUrl,
      resourceId: this.resourceId || this.sourceUrl || '',
      resourceType: this.resourceType,
      route: normalizeRoute(this.subPath),
      isNavOwner: this.navOwner,
      proxyConfig: this.proxyConfig,
      configOverrides: this.configOverrides,
    })
  }

  private buildChildConfig() {
    return buildInjectedChildConfig({
      resourceId: this.resourceId || this.sourceUrl || '',
      resourceType: this.resourceType,
      route: normalizeRoute(this.subPath),
      isNavOwner: this.navOwner,
      configOverrides: this.configOverrides,
      proxyConfig: this.proxyConfig,
    })
  }

  private renderIframe(html: string) {
    const iframe = document.createElement('iframe')
    iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-forms allow-popups allow-downloads')
    // Identify this srcdoc frame for dev tools / AI DOM inspectors.
    iframe.name = wippyFrameName(this.resourceType, this.resourceId || '')
    iframe.title = wippyFrameTitle(this.resourceType, this.resourceId || '', this.resourceLabel)
    iframe.style.width = '100%'
    iframe.setAttribute('part', 'frame')

    return new Promise<void>((resolve, reject) => {
      iframe.addEventListener('load', () => resolve(), { once: true })
      iframe.addEventListener('error', () => reject(new Error('Iframe failed to load')), { once: true })
      this.shadow.appendChild(iframe)
      this.iframe = iframe
      this.setupMessageBridge()
      this.emitNavOwnerClaim()
      iframe.srcdoc = html
    })
  }

  private postUrlUpdate(path: string) {
    this.postToChild(IFrameMessageType.UrlWasUpdatedInParent, {
      path: path.startsWith('/') ? path : `/${path}`,
    })
  }

  private postToChild(action: IFrameMessageType, payload?: Record<string, unknown>) {
    this.iframe?.contentWindow?.postMessage(JSON.stringify({
      type: IFRAME_MESSAGE_TYPE,
      action,
      ...payload,
    }), '*')
  }

  /**
   * Track a window message listener disposed on success, timeout, or
   * cleanup(). Returns the disposer so cleanup() can drain accumulated
   * relays on rapid src changes.
   */
  private trackRelay({ ttlMs, handler, onTimeout }: {
    ttlMs: number
    handler: (data: any) => boolean
    onTimeout: () => void
  }): () => void {
    let disposed = false
    let timer: ReturnType<typeof setTimeout>
    // Forward-declared so `dispose` can reference it (mutual recursion).
    let listener: (event: MessageEvent) => void

    const dispose = () => {
      if (disposed)
        return
      disposed = true
      clearTimeout(timer)
      window.removeEventListener('message', listener)
      const idx = this.activeRelayCleanups.indexOf(dispose)
      if (idx !== -1)
        this.activeRelayCleanups.splice(idx, 1)
    }

    listener = (event: MessageEvent) => {
      if (typeof event.data !== 'string')
        return
      let data: any
      try {
        data = JSON.parse(event.data)
      }
      catch {
        return
      }
      if (data?.type !== IFRAME_MESSAGE_TYPE)
        return
      if (handler(data))
        dispose()
    }

    window.addEventListener('message', listener)
    timer = setTimeout(() => {
      try {
        onTimeout()
      }
      finally {
        dispose()
      }
    }, ttlMs)

    this.activeRelayCleanups.push(dispose)
    return dispose
  }

  private respondToChildBridgeRequest(sourceWindow: Window, requestId: string, result?: unknown, error?: unknown) {
    sourceWindow.postMessage(JSON.stringify({
      type: IFRAME_MESSAGE_TYPE,
      action: IFrameMessageType.OnBridgeResponse,
      uuid: requestId,
      result,
      error,
    }), '*')
  }

  private postConfigUpdate() {
    const sourceWindow = this.iframe?.contentWindow
    if (!sourceWindow)
      return
    sourceWindow.postMessage(JSON.stringify({
      ...this.buildChildConfig(),
      type: IFRAME_MESSAGE_TYPE,
      action: IFrameMessageType.SetConfig,
    }), '*')
  }

  private setupMessageBridge() {
    const sourceWindow = this.iframe?.contentWindow
    if (!sourceWindow)
      return

    this.messageListener = async (event: MessageEvent) => {
      if (event.source !== sourceWindow)
        return
      if (typeof event.data !== 'string')
        return

      let data: any
      try {
        data = JSON.parse(event.data)
      }
      catch {
        return
      }
      if (data.type !== IFRAME_MESSAGE_TYPE)
        return

      const action: IFrameMessageType = data.action
      const rest = { ...data }
      delete rest.type
      delete rest.action

      switch (action) {
        case IFrameMessageType.CmdBodySize:
          if (this.autoHeight && this.iframe) {
            const { height } = rest
            if (typeof height === 'number')
              this.iframe.style.height = `${height}px`
          }
          break
        case IFrameMessageType.CmdBridgePost: {
          this.dispatchEvent(new CustomEvent('wippy-message', {
            detail: { channel: rest.channel, payload: rest.payload },
            bubbles: true,
            composed: true,
          }))
          break
        }
        case IFrameMessageType.CmdBridgeRequest: {
          const requestId = rest.uuid
          if (!requestId)
            break
          let settled = false
          this.dispatchEvent(new CustomEvent('wippy-message', {
            detail: {
              channel: rest.channel,
              payload: rest.payload,
              requestId,
              respond: (result?: unknown) => {
                if (settled)
                  return
                settled = true
                this.respondToChildBridgeRequest(sourceWindow, requestId, result)
              },
              reject: (error?: unknown) => {
                if (settled)
                  return
                settled = true
                this.respondToChildBridgeRequest(sourceWindow, requestId, undefined, error instanceof Error ? error.message : error)
              },
            },
            bubbles: true,
            composed: true,
          }))
          break
        }
        case IFrameMessageType.OnBridgeResponse: {
          const pending = this.pendingBridgeRequests.get(rest.uuid)
          if (!pending)
            break
          this.pendingBridgeRequests.delete(rest.uuid)
          clearTimeout(pending.timer)
          if (rest.error != null)
            pending.reject(new Error(String(rest.error)))
          else
            pending.resolve(rest.result)
          break
        }
        case IFrameMessageType.CmdStartChat: {
          const { start_token, ...options } = rest
          if (!start_token)
            break
          const { host } = await window.getWippyApi()
          host.startChat(start_token, options)
          break
        }
        case IFrameMessageType.CmdOpenSession: {
          const { uuid, ...options } = rest
          if (!uuid)
            break
          const { host } = await window.getWippyApi()
          host.openSession(uuid, options)
          break
        }
        case IFrameMessageType.CmdOpenArtifact: {
          const { uuid, ...options } = rest
          if (!uuid)
            break
          const { host } = await window.getWippyApi()
          host.openArtifact(uuid, options)
          break
        }
        case IFrameMessageType.CmdNavigate: {
          const { url } = rest
          if (url) {
            const { host } = await window.getWippyApi()
            host.navigate(url)
          }
          break
        }
        case IFrameMessageType.CmdSetThemeMode: {
          // Relay a nested child's theme-mode request up to our own host. The
          // resulting change broadcasts back down via SetConfig (which the proxy
          // re-applies through buildChildPayload at each nesting layer).
          const { mode } = rest
          if (mode === 'auto' || mode === 'light' || mode === 'dark') {
            const { host } = await window.getWippyApi()
            host.setThemeMode(mode)
          }
          break
        }
        case IFrameMessageType.CmdRouteChanged: {
          if (this.navOwner) {
            this.dispatchEvent(new CustomEvent('nav-owner-route', {
              detail: { path: rest.internalRoute || '/', navId: rest.navId },
              bubbles: true,
              composed: true,
            }))
          }
          break
        }
        case IFrameMessageType.CmdHandleError: {
          const { code = 'other', error } = rest
          const { host } = await window.getWippyApi()
          host.handleError(code, error)
          break
        }
        case IFrameMessageType.CmdShowToast: {
          const { host } = await window.getWippyApi()
          host.toast(rest)
          break
        }
        case IFrameMessageType.CmdShowConfirm: {
          const { uuid: confirmUuid, ...options } = rest
          if (!confirmUuid)
            break
          const { host } = await window.getWippyApi()
          try {
            const res = await host.confirm(options)
            sourceWindow.postMessage(JSON.stringify({ type: IFRAME_MESSAGE_TYPE, action: IFrameMessageType.OnConfirmResult, uuid: confirmUuid, result: res }), '*')
          }
          catch {
            sourceWindow.postMessage(JSON.stringify({ type: IFRAME_MESSAGE_TYPE, action: IFrameMessageType.OnConfirmResult, uuid: confirmUuid, result: false }), '*')
          }
          break
        }
        case IFrameMessageType.CmdSetContext: {
          const { context, sessionUUID } = rest
          if (!context) {
            break
          }
          ;(await window.getWippyApi()).host.setContext(context, sessionUUID, {
            uuid: this.resourceId || this.sourceUrl || '',
            type: this.resourceType,
          })
          break
        }
        case IFrameMessageType.CmdLogout: {
          (await window.getWippyApi()).host.logout()
          break
        }
        case IFrameMessageType.CmdSubscribe: {
          const topic = rest.topic
          if (!topic)
            break
          if (this.subscriptions[topic])
            return
          const { on } = await window.getWippyApi()
          const off = on(topic as string, (message: any) => {
            sourceWindow.postMessage(JSON.stringify({ type: IFRAME_MESSAGE_TYPE, action: IFrameMessageType.OnSubscription, message }), '*')
          }) as unknown as () => void
          this.subscriptions[topic] = { topic, off }
          break
        }
        case IFrameMessageType.CmdUnSubscribe: {
          const topic = rest.topic
          if (!topic)
            break
          const rec = this.subscriptions[topic]
          if (rec) {
            rec.off()
            delete this.subscriptions[topic]
          }
          break
        }
        case IFrameMessageType.GetConfig: {
          sourceWindow.postMessage(JSON.stringify({
            ...this.buildChildConfig(),
            type: IFRAME_MESSAGE_TYPE,
            action: IFrameMessageType.SetConfig,
          }), '*')
          break
        }
        case IFrameMessageType.CmdWsSend: {
          if (rest.uuid) {
            const innerUuid = rest.uuid
            const outerUuid = uuid()
            window.parent.postMessage(JSON.stringify({
              type: IFRAME_MESSAGE_TYPE,
              action,
              command: rest.command,
              uuid: outerUuid,
            }), '*')

            // Build the cleanup hook BEFORE wiring the listener/timeout so
            // the success path, the timeout path, and the cleanup() drain
            // all dispose through the same single function — no
            // duplicated removeEventListener/clearTimeout sites.
            const dispose = this.trackRelay({
              ttlMs: 30_000,
              handler: (respData) => {
                if (
                  respData.action === IFrameMessageType.OnWsResult
                  && respData.uuid === outerUuid
                ) {
                  sourceWindow.postMessage(JSON.stringify({
                    type: IFRAME_MESSAGE_TYPE,
                    action: IFrameMessageType.OnWsResult,
                    uuid: innerUuid,
                    result: respData.result,
                    error: respData.error,
                  }), '*')
                  return true
                }
                return false
              },
              onTimeout: () => {
                sourceWindow.postMessage(JSON.stringify({
                  type: IFRAME_MESSAGE_TYPE,
                  action: IFrameMessageType.OnWsResult,
                  uuid: innerUuid,
                  error: 'WebSocket relay timed out',
                }), '*')
              },
            })
            void dispose
          }
          else {
            window.parent.postMessage(JSON.stringify({
              type: IFRAME_MESSAGE_TYPE,
              action,
              ...rest,
            }), '*')
          }
          break
        }
        case IFrameMessageType.CmdLog: {
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action,
            ...rest,
            source: {
              ...rest.source,
              nestingDepth: (rest.source?.nestingDepth ?? 0) + 1,
            },
          }), '*')
          break
        }
        case IFrameMessageType.CmdStateSet:
        case IFrameMessageType.CmdStateRemove:
        case IFrameMessageType.CmdStateClear: {
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action,
            ...rest,
            scope: rest.scope || this.resourceId,
          }), '*')
          break
        }
        case IFrameMessageType.CmdStateGet:
        case IFrameMessageType.CmdStateGetAll: {
          const { uuid: innerUuid, ...stateRest } = rest
          if (!innerUuid)
            break
          const outerUuid = uuid()
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action,
            ...stateRest,
            uuid: outerUuid,
            scope: rest.scope || this.resourceId,
          }), '*')

          // Relay disposes on success, timeout, OR cleanup(). Timeout
          // responds to the child — without it, child `state.get(...)`
          // hangs until its own 10s timeout.
          const dispose = this.trackRelay({
            ttlMs: 10_000,
            handler: (respData) => {
              if (
                respData.action === IFrameMessageType.OnStateResult
                && respData.uuid === outerUuid
              ) {
                sourceWindow.postMessage(JSON.stringify({
                  type: IFRAME_MESSAGE_TYPE,
                  action: IFrameMessageType.OnStateResult,
                  uuid: innerUuid,
                  value: respData.value,
                }), '*')
                return true
              }
              return false
            },
            onTimeout: () => {
              sourceWindow.postMessage(JSON.stringify({
                type: IFRAME_MESSAGE_TYPE,
                action: IFrameMessageType.OnStateResult,
                uuid: innerUuid,
                error: 'State relay timed out',
              }), '*')
            },
          })
          void dispose
          break
        }
        case IFrameMessageType.CmdTitleChanged:
        case IFrameMessageType.CmdLayoutResizePanel:
        case IFrameMessageType.CmdLayoutCollapsePanel:
        case IFrameMessageType.CmdLayoutExpandPanel:
        case IFrameMessageType.CmdLayoutMovePanel:
        case IFrameMessageType.CmdLayoutRemovePanel:
        case IFrameMessageType.CmdLayoutUpdatePanel:
        case IFrameMessageType.CmdLayoutAddFloating:
        case IFrameMessageType.CmdLayoutRemoveFloating:
        case IFrameMessageType.CmdLayoutOpenModal:
        case IFrameMessageType.CmdLayoutCloseModal:
        case IFrameMessageType.CmdLayoutBroadcast:
        case IFrameMessageType.CmdLayoutOpenDrawer:
        case IFrameMessageType.CmdLayoutCloseDrawer:
        case IFrameMessageType.CmdLayoutToggleDrawer: {
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action,
            ...rest,
          }), '*')
          break
        }
        case IFrameMessageType.CmdLayoutSubscribe: {
          this.layoutSubCount += 1
          if (this.layoutSubCount > 1)
            break
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action: IFrameMessageType.CmdLayoutSubscribe,
          }), '*')
          this.layoutEventListener = (evt: MessageEvent) => {
            if (evt.source !== window.parent)
              return
            if (typeof evt.data !== 'string')
              return
            let payload: any
            try {
              payload = JSON.parse(evt.data)
            }
            catch {
              return
            }
            if (payload?.type !== IFRAME_MESSAGE_TYPE)
              return
            if (
              payload.action === IFrameMessageType.OnLayoutChange
              || payload.action === IFrameMessageType.OnLayoutPanelChanged
              || payload.action === IFrameMessageType.OnLayoutBreakpoint
              || payload.action === IFrameMessageType.OnLayoutBroadcast
            ) {
              sourceWindow.postMessage(evt.data, '*')
            }
          }
          window.addEventListener('message', this.layoutEventListener)
          break
        }
        case IFrameMessageType.CmdLayoutUnsubscribe: {
          if (this.layoutSubCount <= 0)
            break
          this.layoutSubCount -= 1
          if (this.layoutSubCount > 0)
            break
          if (this.layoutEventListener) {
            window.removeEventListener('message', this.layoutEventListener)
            this.layoutEventListener = undefined
          }
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action: IFrameMessageType.CmdLayoutUnsubscribe,
          }), '*')
          break
        }
        default:
          break
      }
    }

    window.addEventListener('message', this.messageListener)
  }

  private setStatus(status: 'loading' | 'ready' | 'error') {
    this.setAttribute('status', status)
  }
}

if (!customElements.get('w-iframe'))
  customElements.define('w-iframe', WIframeElement)
